import { PageHero } from '@components/common';
import { getServiceCard } from '@data/serviceCards';
import {
  Siren,
  Users,
  FileText,
  CheckCircle,
  ArrowRight,
  ShieldAlert,
  Phone,
  Clock,
  Stethoscope,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { CALENDLY_URL } from '@constants';

const offerings = [
  {
    icon: FileText,
    title: 'IR Plan Development',
    description:
      'Comprehensive incident response plans tailored to your organisation, covering detection, containment, eradication, and recovery.',
  },
  {
    icon: Users,
    title: 'Role Definition',
    description:
      'Clear RACI matrices, contact trees, and escalation procedures ensuring everyone knows their responsibilities during an incident.',
  },
  {
    icon: Siren,
    title: 'Tabletop Exercises',
    description:
      'Realistic simulations that test your response capabilities, identify gaps, and build muscle memory for your team.',
  },
  {
    icon: Phone,
    title: 'Breach Response Support',
    description:
      'On-call support during actual incidents, providing guidance and coordination when you need it most.',
  },
];

const whenYouNeedIt = [
  'You do not have a documented incident response plan',
  'Your current plan has not been tested or updated recently',
  'Recent organisational changes have affected response roles',
  'You experienced a security incident and identified gaps',
  <>
    Preparing for{' '}
    <Link to="/services/compliance" className="text-secondary hover:underline">
      compliance certification
    </Link>{' '}
    (SOC 2, ISO 27001)
  </>,
  'Board or investors require evidence of IR capabilities',
  'Want to reduce incident impact and recovery time',
];

const phases = [
  {
    phase: 'Preparation',
    description: 'Tools, training, and plans in place before an incident occurs',
    activities: ['IR plan documentation', 'Team training', 'Tool deployment', 'Playbook creation'],
  },
  {
    phase: 'Detection & Analysis',
    description: 'Identifying and confirming security incidents quickly',
    activities: ['Monitoring setup', 'Alert triage', 'Initial assessment', 'Incident declaration'],
  },
  {
    phase: 'Containment',
    description: 'Stopping the incident from spreading and limiting damage',
    activities: [
      'Short-term containment',
      'System isolation',
      'Evidence preservation',
      'Communication',
    ],
  },
  {
    phase: 'Eradication',
    description: 'Removing the root cause and threat actor access',
    activities: [
      'Threat removal',
      'Vulnerability patching',
      'Access revocation',
      'System hardening',
    ],
  },
  {
    phase: 'Recovery',
    description: 'Restoring systems and returning to normal operations',
    activities: [
      'System restoration',
      'Monitoring enhancement',
      'Verification testing',
      'Service resumption',
    ],
  },
  {
    phase: 'Lessons Learned',
    description: 'Improving for next time through review and documentation',
    activities: ['Post-incident review', 'Report creation', 'Process improvement', 'Plan updates'],
  },
];

export default function IncidentResponsePage() {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Incident Response Planning',
    description: 'Be prepared when breaches occur. We help you build and test response plans',
    url: 'https://thecisonetwork.com/services/incident-response',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <PageHero
        title="Incident Response Planning"
        subtitle="Be prepared when breaches occur. We help you build and test response plans that minimise damage and recovery time."
        eyebrow="Our Services"
        backgroundImage={getServiceCard('incident-response')?.image}
        backgroundAlt={getServiceCard('incident-response')?.imageAlt}
        seoTitle="Incident Response Planning | The CISO Network"
        seoDescription="Expert incident response planning and tabletop exercises. Develop IR plans, define roles, and prepare your team for security incidents."
      />

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                It Is Not If, But When
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Every organisation will face a security incident eventually. The difference
                  between a minor disruption and a major crisis often comes down to preparation.
                  Organisations with tested incident response plans recover faster, reduce damage,
                  and maintain stakeholder confidence.
                </p>
                <p>
                  We help you develop comprehensive incident response capabilities tailored to your
                  organisation. From documented plans and clear procedures to realistic exercises
                  that build team readiness, we ensure you are prepared when incidents occur.
                </p>
                <p>
                  Our fractional CISOs bring experience from real incidents, providing practical
                  guidance that works under pressure. We focus on actionable plans that your team
                  can actually execute, not theoretical documents that sit on a shelf.
                </p>
                <p>
                  For organisations that want to go beyond response planning and ensure they can
                  continue operating through disruption, we also provide{' '}
                  <Link to="/services/cyber-resilience" className="text-secondary hover:underline">
                    cyber resilience and business continuity
                  </Link>{' '}
                  services.
                </p>
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <div className="flex items-center gap-4 mb-6 justify-center">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center">
                  <ShieldAlert className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">
                  When You Need This
                </h3>
              </div>
              <div className="flex justify-center">
                <ul className="space-y-4 inline-block text-left">
                  {whenYouNeedIt.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The NIST Framework */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              The Incident Response Lifecycle
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Based on the NIST Cybersecurity Framework, we help you prepare for and manage every
              phase of incident response
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {phases.map((phase, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-8 w-8 rounded-full bg-teal/10 flex items-center justify-center">
                    <span className="text-teal font-bold text-sm">{i + 1}</span>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground">{phase.phase}</h3>
                </div>
                <p className="text-muted-foreground text-sm mb-4">{phase.description}</p>
                <ul className="space-y-2">
                  {phase.activities.map((activity, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <div className="w-1 h-1 rounded-full bg-teal" />
                      {activity}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Incident Response Services
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Comprehensive support for building and maintaining IR capabilities
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {offerings.map((offering, i) => (
              <div key={i} className="bg-background rounded-2xl p-8 border border-border">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center mb-6">
                  <offering.icon className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                  {offering.title}
                </h3>
                <p className="text-muted-foreground">{offering.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Retainer */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 text-teal text-sm font-medium mb-6">
                <Clock className="h-4 w-4" />
                Available 24/7
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Incident Response Retainer
              </h2>
              <p className="text-muted-foreground mb-6">
                For organisations that want guaranteed access to senior security expertise during
                incidents, we offer an incident response retainer. This provides peace of mind that
                experienced support is just a phone call away when you need it most.
              </p>
              <div className="flex justify-center">
                <ul className="space-y-3 inline-block text-left">
                  {[
                    'Guaranteed response times during incidents',
                    'Pre-established relationship and context',
                    'Immediate guidance and coordination support',
                    'Post-incident review and improvement recommendations',
                    'Regular plan updates and testing included',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-border">
              <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center mb-6">
                <Stethoscope className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                Incident Response Health Check
              </h3>
              <p className="text-muted-foreground mb-6">
                Not sure where to start? Our IR health check rapidly assesses your current incident
                response readiness and provides a clear improvement roadmap.
              </p>
              <div className="space-y-4">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium text-foreground">1-2 weeks</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Deliverable</span>
                  <span className="font-medium text-foreground">Gap analysis & roadmap</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Output</span>
                  <span className="font-medium text-foreground">Prioritised action plan</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Be Prepared for the Inevitable
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a discovery call to discuss your incident response needs and how our{' '}
            <Link to="/what-is-a-fractional-ciso" className="text-teal hover:underline">
              fractional CISOs
            </Link>{' '}
            can help you prepare.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
